import { GA4_DEBUG_MODE } from '@/constants'
import type { VisitorData } from '@/types/analytics'

interface SessionData {
	pageViews: number // Only track this for "High Session Engagement" audience
	sessionStart: string
}

const VISITOR_STORAGE_KEY = 'ga4_visitor_data'
const SESSION_STORAGE_KEY = 'ga4_session_data'
const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes

const generateId = (prefix: string): string => {
	return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

const daysBetween = (date1: string, date2: string): number => {
	const d1 = new Date(date1)
	const d2 = new Date(date2)
	const diffTime = Math.abs(d2.getTime() - d1.getTime())
	return Math.floor(diffTime / (1000 * 60 * 60 * 24))
}

const consoleLog = (...args: any[]) => {
	if (import.meta.client && GA4_DEBUG_MODE) {
		console.log('[GA4]', ...args)
	}
}

export const useVisitorTracking = () => {
	const visitorData = useState<VisitorData | null>('visitor-data', () => null)
	const sessionData = useState<SessionData | null>('session-data', () => null)
	const isNewVisitor = useState('is-new-visitor', () => true)

	const loadVisitorData = (): VisitorData | null => {
		if (!import.meta.client) return null
		try {
			const stored = localStorage.getItem(VISITOR_STORAGE_KEY)
			return stored ? JSON.parse(stored) : null
		} catch {
			return null
		}
	}

	const saveVisitorData = (data: VisitorData) => {
		if (!import.meta.client) return
		try {
			localStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(data))
		} catch {
			consoleLog('Failed to save visitor data to localStorage')
		}
	}

	const loadSessionData = (): SessionData | null => {
		if (!import.meta.client) return null
		const stored = sessionStorage?.getItem(SESSION_STORAGE_KEY)
		if (stored) {
			const data = JSON.parse(stored)
			const sessionAge = Date.now() - new Date(data.sessionStart).getTime()
			return sessionAge < SESSION_TIMEOUT ? data : null
		}
		return null
	}

	const saveSessionData = (data: SessionData) => {
		if (!import.meta.client) return
		sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data))
	}

	const initializeVisitor = () => {
		if (!import.meta.client) return

		const now = new Date().toISOString()
		const today = now.split('T')[0]
		const existingData = loadVisitorData()

		if (existingData) {
			isNewVisitor.value = false
			const lastVisitDate = existingData.lastVisit.split('T')[0]
			const isNewDay = lastVisitDate !== today

			const updatedData: VisitorData = {
				...existingData,
				lastVisit: now,
				visitCount: isNewDay ? existingData.visitCount + 1 : existingData.visitCount,
				visitHistory: [], // Not needed for audiences
				returningVisitor: true,
				daysSinceFirstVisit: daysBetween(existingData.firstVisit, now),
				daysSinceLastVisit: daysBetween(existingData.lastVisit, now)
			}

			visitorData.value = updatedData
			saveVisitorData(updatedData)
		} else {
			isNewVisitor.value = true
			const newData: VisitorData = {
				visitorId: generateId('v'),
				firstVisit: now,
				lastVisit: now,
				visitCount: 1,
				visitHistory: [],
				returningVisitor: false,
				daysSinceFirstVisit: 0,
				daysSinceLastVisit: 0
			}

			visitorData.value = newData
			saveVisitorData(newData)
		}

		// Initialize session
		const existingSession = loadSessionData()
		if (existingSession) {
			sessionData.value = existingSession
		} else {
			sessionData.value = {
				pageViews: 1,
				sessionStart: now
			}
			saveSessionData(sessionData.value)
		}
	}

	// Only return properties needed for audiences
	const getAudienceProperties = () => {
		const properties: Record<string, any> = {}

		if (visitorData.value) {
			properties.visitor_type = visitorData.value.returningVisitor ? 'returning' : 'new'
			properties.visitor_segment = getVisitorSegment()
			properties.visitor_days_since_last = visitorData.value.daysSinceLastVisit
		}

		if (sessionData.value) {
			properties.session_page_views = sessionData.value.pageViews
		}

		return properties
	}

	const getVisitorSegment = (): string => {
		if (!visitorData.value) return 'unknown'
		const {
			visitCount, daysSinceFirstVisit
		} = visitorData.value

		if (visitCount === 1) return 'first_time'
		if (visitCount <= 3) return 'new_returning'
		if (visitCount <= 10) return 'engaged'
		if (daysSinceFirstVisit > 30 && visitCount > 10) return 'loyal'
		return 'frequent'
	}

	const clearAllData = () => {
		if (!import.meta.client) return
		localStorage.removeItem(VISITOR_STORAGE_KEY)
		sessionStorage.removeItem(SESSION_STORAGE_KEY)
		visitorData.value = null
		sessionData.value = null
		isNewVisitor.value = true
	}

	return {
		visitorData: readonly(visitorData),
		isNewVisitor: readonly(isNewVisitor),
		initializeVisitor,
		getAudienceProperties,
		getVisitorSegment,
		clearAllData
	}
}
